import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { fetchParentProfile } from "@/api";
import type { ApiLeadCaptureResponse } from "@/api/types";
import Modal from "../ui/Modal";

interface LeadCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    parentName?: string;
    phone?: string;
    countryCode?: string;
    email?: string;
    password?: string;
    childName?: string;
    childDob?: string;
    childGender?: string;
    childId?: string;
  }) => Promise<ApiLeadCaptureResponse>;
}

const countryCodes = [
  { code: "+966", flag: "🇸🇦", nameKey: "countrySaudiArabia" },
  { code: "+971", flag: "🇦🇪", nameKey: "countryUae" },
  { code: "+965", flag: "🇰🇼", nameKey: "countryKuwait" },
  { code: "+974", flag: "🇶🇦", nameKey: "countryQatar" },
  { code: "+973", flag: "🇧🇭", nameKey: "countryBahrain" },
  { code: "+968", flag: "🇴🇲", nameKey: "countryOman" },
  { code: "+962", flag: "🇯🇴", nameKey: "countryJordan" },
  { code: "+20", flag: "🇪🇬", nameKey: "countryEgypt" },
  { code: "+961", flag: "🇱🇧", nameKey: "countryLebanon" },
  { code: "+963", flag: "🇸🇾", nameKey: "countrySyria" },
  { code: "+964", flag: "🇮🇶", nameKey: "countryIraq" },
  { code: "+970", flag: "🇵🇸", nameKey: "countryPalestine" },
  { code: "+212", flag: "🇲🇦", nameKey: "countryMorocco" },
  { code: "+216", flag: "🇹🇳", nameKey: "countryTunisia" },
  { code: "+213", flag: "🇩🇿", nameKey: "countryAlgeria" },
  { code: "+249", flag: "🇸🇩", nameKey: "countrySudan" },
  { code: "+252", flag: "🇸🇴", nameKey: "countrySomalia" },
  { code: "+967", flag: "🇾🇪", nameKey: "countryYemen" },
  { code: "+1", flag: "🇺🇸", nameKey: "countryUsa" },
  { code: "+44", flag: "🇬🇧", nameKey: "countryUk" },
];

const isValidReportId = (value: unknown): value is string => {
  return typeof value === "string" && Boolean(value.trim()) && value !== "null" && value !== "undefined";
};

export default function LeadCaptureModal({ isOpen, onClose, onSubmit }: LeadCaptureModalProps) {
  const { t, i18n } = useTranslation("common");
  const isRTL = i18n.language === "ar";

  const [step, setStep] = useState<"form" | "success">("form");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [childrenList, setChildrenList] = useState<any[]>([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [addChildMode, setAddChildMode] = useState(false);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Parent form states
  const [parentName, setParentName] = useState(() => sessionStorage.getItem("savedParentName") || "");
  const [countryCode, setCountryCode] = useState(() => sessionStorage.getItem("savedParentCountryCode") || "+966");
  const [phone, setPhone] = useState(() => sessionStorage.getItem("savedParentPhone") || "");
  const [email, setEmail] = useState(() => sessionStorage.getItem("savedParentEmail") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Child form states
  const [childName, setChildName] = useState("");
  const [childDob, setChildDob] = useState("");
  const [childGender, setChildGender] = useState("male");

  const [savedReportId, setSavedReportId] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setStep("form");
    setError("");
    const token = localStorage.getItem("parent_token");
    setIsLoggedIn(!!token);

    if (token) {
      setLoadingChildren(true);
      fetchParentProfile()
        .then((profile) => {
          setChildrenList(profile.children || []);
          if (profile.children && profile.children.length > 0) {
            setSelectedChildId(profile.children[0].id);
            setAddChildMode(false);
          } else {
            setAddChildMode(true);
          }
        })
        .catch((err) => {
          console.error("Failed to load children", err);
          // Token expired or invalid
          localStorage.removeItem("parent_token");
          localStorage.removeItem("parent_name");
          setIsLoggedIn(false);
          setAddChildMode(true);
        })
        .finally(() => {
          setLoadingChildren(false);
        });
    } else {
      setAddChildMode(true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const validateForm = () => {
    if (!isLoggedIn) {
      if (!parentName.trim()) {
        setError(t("parentNameRequired"));
        return false;
      }
      if (!phone.trim() || phone.length < 8) {
        setError(t("validPhoneRequired"));
        return false;
      }
      if (!email.trim() || !email.includes("@")) {
        setError(t("validEmailMandatory"));
        return false;
      }
      if (!password.trim() || password.length < 6) {
        setError(t("accountPasswordRequired"));
        return false;
      }
    }

    if (addChildMode) {
      if (!childName.trim()) {
        setError(t("childNameRequired"));
        return false;
      }
      if (!childDob) {
        setError(t("childDobRequired"));
        return false;
      }
    } else {
      if (!selectedChildId) {
        setError(t("selectChildRequired"));
        return false;
      }
    }

    setError("");
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError("");

    try {
      const result = await onSubmit({
        parentName: isLoggedIn ? undefined : parentName,
        phone: isLoggedIn ? undefined : phone,
        countryCode: isLoggedIn ? undefined : countryCode,
        email: isLoggedIn ? undefined : email,
        password: isLoggedIn ? undefined : password,
        childName: addChildMode ? childName : undefined,
        childDob: addChildMode ? childDob : undefined,
        childGender: addChildMode ? childGender : undefined,
        childId: addChildMode ? undefined : selectedChildId,
      });

      if (result.token) {
        localStorage.setItem("parent_token", result.token);
        localStorage.setItem("parent_name", parentName || t("parentDefaultName"));
      }

      if (!isValidReportId(result.reportId)) {
        throw new Error("missing_report_id");
      }

      setSavedReportId(result.reportId);
      setStep("success");
    } catch (err: any) {
      console.error(err);
      if (err.message === "phone_already_registered") {
        setError(t("phoneAlreadyRegistered"));
      } else if (err.message === "email_already_registered") {
        setError(t("emailAlreadyRegistered"));
      } else {
        setError(t("saveReportFailed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFinalNavigate = (addAnotherChild: boolean) => {
    onClose();
    if (addAnotherChild) {
      sessionStorage.removeItem("assessmentData");
      window.location.href = "/";
    } else if (isValidReportId(savedReportId)) {
      window.location.href = `/report/${savedReportId}`;
    } else {
      setError(t("saveReportFailed"));
      setStep("form");
    }
  };

  const selectedCountry = countryCodes.find((c) => c.code === countryCode);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        step === "form"
          ? t("savePrintReport")
          : t("reportSavedSuccess")
      }
      maxWidth="lg"
    >
      {error && (
        <div className="mb-4 p-3.5 bg-red-50 text-red-500 rounded-xl text-xs flex items-center gap-2">
          <i className="ri-error-warning-line text-sm" />
          <span>{error}</span>
        </div>
      )}

      {step === "form" && (
        <div className="space-y-4">
          {/* If Logged In: show Child Selection */}
          {isLoggedIn ? (
            <div>
              {loadingChildren ? (
                <div className="text-center py-4 text-gray-400 text-sm">{t("loading")}</div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-start">
                    {t("selectChildForReport")} <span className="text-red-400">*</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {childrenList.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedChildId(c.id);
                          setAddChildMode(false);
                        }}
                        className={`w-full flex items-center justify-between p-3.5 rounded-xl border text-sm font-medium transition-all ${
                          !addChildMode && selectedChildId === c.id
                            ? "border-primary-400 bg-primary-400/10 text-primary-500"
                            : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <i className={c.gender === "male" ? "ri-men-line text-blue-500" : "ri-women-line text-pink-500"} />
                          <span>{c.name}</span>
                        </div>
                        <span className="text-xs text-gray-400">({calculateAge(c.dob, t)})</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setAddChildMode(true)}
                      className={`w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border border-dashed text-sm font-medium transition-all ${
                        addChildMode
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-add-circle-line" />
                      <span>{t("addNewChildForReport")}</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* If Not Logged In: show Parent registration inputs */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                  {t("parentName")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={parentName}
                  onChange={(e) => setParentName(e.target.value)}
                  placeholder={t("parentNamePlaceholder")}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                  {t("parentPhone")} <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="flex items-center gap-1 px-3 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm whitespace-nowrap hover:bg-gray-100 transition-colors"
                    >
                      <span>{selectedCountry?.flag}</span>
                      <span className="text-gray-700">{countryCode}</span>
                      <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute top-full mt-1 left-0 w-48 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg z-20">
                        {countryCodes.map((c) => (
                          <button
                            type="button"
                            key={c.code}
                            onClick={() => {
                              setCountryCode(c.code);
                              setShowCountryDropdown(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors text-left"
                          >
                            <span>{c.flag}</span>
                            <span className="text-gray-700">{t(c.nameKey)}</span>
                            <span className="text-gray-400 mr-auto">{c.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder={t("phonePlaceholder")}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                  {t("parentEmail")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@domain.com"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                  {t("newAccountPassword")} <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-1"
                    style={{ [isRTL ? "left" : "right"]: "10px" }}
                    tabIndex={-1}
                  >
                    <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Child Details: Show if adding a child or not logged in */}
          {addChildMode && (
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-bold text-navy-400 text-start">
                {t("childInformation")}
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                  {t("childName")} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  placeholder={t("childNamePlaceholder")}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                    {t("dob")} <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="date"
                    value={childDob}
                    onChange={(e) => setChildDob(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm transition-all text-start"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">
                    {t("gender")} <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setChildGender("male")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        childGender === "male"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-men-line mr-1" />
                      {t("male")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setChildGender("female")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        childGender === "female"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-women-line mr-1" />
                      {t("female")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Note */}
          <div className="bg-primary-400/5 rounded-xl p-3 flex items-start gap-2 text-start">
            <i className="ri-information-line text-primary-400 text-lg flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-600 leading-relaxed">
              {t("saveReportAccountNote")}
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
          >
            {loading ? (
              <span>{t("saving")}</span>
            ) : (
              <>
                <i className="ri-save-line" />
                <span>{t("saveReportLinkAccount")}</span>
              </>
            )}
          </button>
        </div>
      )}

      {step === "success" && (
        <div className="space-y-6 text-center py-4">
          <div className="w-20 h-20 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center mx-auto mb-4 border border-green-200">
            <i className="ri-checkbox-circle-fill text-5xl animate-bounce" />
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => handleFinalNavigate(false)}
              className="w-full py-4 bg-primary-400 hover:bg-primary-500 text-white rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2 shadow-md"
            >
              <i className="ri-file-chart-line text-lg" />
              {t("viewFinalReport")}
            </button>

            <button
              type="button"
              onClick={() => handleFinalNavigate(true)}
              className="w-full py-4 bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 text-navy-400 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
            >
              <i className="ri-user-add-line text-lg" />
              {t("addAnotherChildStartAssessment")}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function calculateAge(dob: string, t: (key: string) => string): string {
  if (!dob) return "";
  const birth = new Date(dob);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years > 0) {
    return `${years} ${years === 1 ? t("yearSingular") : t("yearPlural")} ${remainingMonths > 0 ? `${t("and")} ${remainingMonths} ${t("monthPlural")}` : ""}`;
  }
  return `${months} ${months === 1 ? t("monthSingular") : t("monthPlural")}`;
}
